import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Cms, PAGE_TYPES } from '@impact/data';
import { filter, take, takeUntil } from 'rxjs/operators';
import { getStringParameters } from '../../core/helpers';
import { SettingsService } from '../../core/settings.service';
import { FilterService } from '../../filter-service/filter.service';
import { NavigationService } from '../../navigation/navigation.service';
import { merge, Subject } from 'rxjs';

@Component({
    selector: 'app-filter-page-seo-links',
    template: `
        <section
            class="headline-and-text-spot filter-models-spot spot-spacing"
            *ngIf="isReady && seoLinksPageData"
        >
            <h2
                class="headline-and-text-spot__headline"
                [innerText]="
                    'filterpage.filter_spot_seo_links_header' | translate
                "
            ></h2>

            <div class="headline-and-text-spot__content">
                <ul class="headline-and-text-spot__links-list">
                    <li
                        class="headline-and-text-spot__links-list-item"
                        *ngFor="
                            let page of seoLinksPageData;
                            trackBy: trackByPage
                        "
                    >
                        <ng-container *ngIf="page.template === pageTypes.LINK_PAGE">
                            <a *ngIf="page?.pageLink?.url"
                                class="headline-and-text-spot__links-list-link"
                                [routerLink]="page.pageLink.url"
                                [innerText]="page.name"
                            ></a>
                        </ng-container>

                        <ng-container *ngIf="page.template !== pageTypes.LINK_PAGE">
                            <a *ngIf="page?.url"
                                class="headline-and-text-spot__links-list-link"
                                [routerLink]="page.url"
                                [innerText]="page.name"
                            ></a>
                        </ng-container>
                    </li>
                </ul>
            </div>
        </section>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterPageSeoLinksComponent implements OnInit, OnDestroy {
    private readonly unsubscribe = new Subject<void>();

    isReady: boolean;
    isRunningUpdateLinks: boolean;
    makeLabel: string;
    pageTypes = PAGE_TYPES;
    seoLinksPageData: Cms.NavigationDto[] | null;
    usedCarsOverviewPageUrl: string;
    usedCarsVehicleMakesPage: string;

    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly filterService: FilterService,
        private readonly navigationService: NavigationService,
        private readonly settingsService: SettingsService
    ) {}

    ngOnInit(): void {
        this.settingsService
            .get()
            .pipe(take(1))
            .subscribe((settings) => {
                const { usedCarsOverviewPage, usedCarsVehicleMakesPage } =
                    settings.globalPages;
                this.usedCarsOverviewPageUrl = usedCarsOverviewPage?.url;
                this.usedCarsVehicleMakesPage = usedCarsVehicleMakesPage?.url;
                this.cdr.markForCheck();
            });

        this.navigationService.usedCarsNavigation$
            .pipe(take(1))
            .subscribe((usedCarsNavigation) => {
                this.activateUrlListeners(usedCarsNavigation);
            });
    }

    activateUrlListeners(usedCarsNavigation: Cms.NavigationDto[]) {
        merge(
            this.filterService.filterUrlChange$,
            this.navigationService.currentUrl$
        )
            .pipe(
                filter((url) => !!url),
                takeUntil(this.unsubscribe),
            )
            .subscribe((url) => {
                this.updateLinks(url, usedCarsNavigation);
            });
    }

    updateLinks(currentUrl: string, usedCarsNavigation: Cms.NavigationDto[]) {
        if (this.isRunningUpdateLinks) {
            return;
        }

        this.isRunningUpdateLinks = true;
        const hasParams = !!currentUrl.split('?')[1];

        // Sanitize urlParams and replacing special characters so they can match with umbraco navigation data
        currentUrl = decodeURIComponent(currentUrl)
            .toLowerCase()
            .replace(/[ëé]/g, 'e');
        const urlFacetParams = getStringParameters(currentUrl);
        const cleanCurrentUrl = currentUrl.split('?')[0].toLowerCase();

        this.seoLinksPageData = null;
        let searchResult;

        if (hasParams && urlFacetParams['make.keyword']) {
            const makeParam = decodeURIComponent(
                urlFacetParams['make.keyword']
            );

            // Find make param and get data from iterating usedCarsSeoLinksPageUrl
            const contextUrl = `${this.usedCarsVehicleMakesPage}/${makeParam}`;
            searchResult = this.findPageByUrl(usedCarsNavigation, contextUrl);
        } else if (cleanCurrentUrl === this.usedCarsOverviewPageUrl) {
            this.seoLinksPageData = usedCarsNavigation;
            this.isRunningUpdateLinks = false;
            this.isReady = true;
            this.cdr.markForCheck();
            return;
        } else if (cleanCurrentUrl === this.usedCarsVehicleMakesPage) {
            searchResult = this.findPageByUrl(usedCarsNavigation, currentUrl);
        } else {
            searchResult = this.findPageByUrl(
                usedCarsNavigation,
                cleanCurrentUrl
            );
        }

        // Final resport fallback if all else fails: use the fallback url from Umbraco
        if (!searchResult) {
            searchResult = this.findPageByUrl(
                usedCarsNavigation,
                this.usedCarsVehicleMakesPage
            );
        }

        if (searchResult) {
            if (searchResult.page?.hasChildren) {
                this.seoLinksPageData = searchResult.page.children;
            } else {
                // If page has no children, we get the parent of that page
                const urlSegments = currentUrl.split('/');
                urlSegments.length = urlSegments.length - 1;
                const parentPageUrl = urlSegments.join('/');

                // If page is used cars main page
                if (parentPageUrl !== this.usedCarsOverviewPageUrl) {
                    this.isRunningUpdateLinks = false;
                    this.updateLinks(parentPageUrl, usedCarsNavigation);
                    return;
                } else {
                    this.updateLinks(
                        this.usedCarsVehicleMakesPage,
                        usedCarsNavigation
                    );
                    this.isRunningUpdateLinks = false;
                    return;
                }
            }
        }

        this.isRunningUpdateLinks = false;
        this.isReady = true;

        this.cdr.markForCheck();
    }

    findPageByUrl(pages: Cms.NavigationDto[], targetUrl: string): any | undefined {
        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];

            if (page.url === targetUrl) {
                return { page, index: i, parent: page };
            }

            // If the current page has children, recursively search them
            if (page.children && page.children.length > 0) {
                const childSearchResult = this.findPageByUrl(
                    page.children,
                    targetUrl
                );

                if (childSearchResult) {
                    return { ...childSearchResult, parent: page, index: i };
                }
            }
        }

        // If no match is found, return undefined
        return undefined;
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    trackByPage(idx: number, item: Cms.NavigationDto) {
        return item.id || idx;
    }
}
